import { Fragment, useState, type ComponentType } from "react";
import { Button, Skeleton } from "@vibe/core";
import {
  Basic,
  Calendar,
  Checkbox,
  Connect,
  Doc,
  DropdownChevronLeft,
  Dropdown,
  Email,
  Favorite,
  File,
  Formula,
  Link,
  Location,
  LongText,
  Mirror,
  Mobile,
  NavigationChevronRight,
  Numbers,
  Person,
  Status,
  Tags,
  Text,
  Time,
  Timeline,
} from "@vibe/icons";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import { useSharedColumnsPreview } from "../../hooks/api/useSharedColumnsPreview";
import type { BoardColumn } from "../../utils/sharedColumns";
import type { GroupShape } from "./addItem.types";

type IconType = ComponentType<{ size?: string | number; className?: string }>;

const COLUMN_TYPE_ICONS: Record<string, IconType> = {
  people: Person,
  person: Person,
  status: Status,
  numbers: Numbers,
  date: Calendar,
  hour: Time,
  email: Email,
  phone: Mobile,
  dropdown: Dropdown,
  text: Text,
  rating: Favorite,
  link: Link,
  long_text: LongText,
  timeline: Timeline,
  file: File,
  checkbox: Checkbox,
  board_relation: Connect,
  location: Location,
  tags: Tags,
  doc: Doc,
  formula: Formula,
  mirror: Mirror,
};

// monday's label palette. Each column keeps the same colour every time the dropdown opens.
const MONDAY_COLORS = [
  "#00c875",
  "#9cd326",
  "#cab641",
  "#ffcb00",
  "#784bd1",
  "#a25ddc",
  "#0086c0",
  "#579bfc",
  "#66ccff",
  "#bb3354",
  "#df2f4a",
  "#ff007f",
  "#ff5ac4",
  "#ff642e",
  "#fdab3d",
  "#7f5347",
  "#037f4c",
  "#401694",
  "#225091",
  "#175a63",
];

const colorFor = (columnId: string) => {
  let hash = 0;
  for (const char of columnId) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return MONDAY_COLORS[Math.abs(hash) % MONDAY_COLORS.length];
};

const HEADER_CLASS =
  "bg-[#FFFFFF] sticky top-0 z-[1] border-b border-[#C3C6D4] overflow-x-hidden add-item-dropdown";
const HEADER_PADDING = { padding: "15px 8px 12px 8px" };
// Tailwind preflight is off in this app, so `border` alone sets a width with no style and draws
// nothing; every bordered element below also needs `border-solid` or `border-dashed`.
const FONT = { fontFamily: "Poppins" };

function ColumnChip({ column, muted }: { column: BoardColumn; muted?: boolean }) {
  const Icon = COLUMN_TYPE_ICONS[column.type] ?? Basic;
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-md border py-[3px] pl-[3px] pr-2 text-xs ${
        muted
          ? "border-dashed border-[#C3C6D4] text-[#676879]"
          : "border-solid border-[#C3C6D4] bg-white text-[#323338]"
      }`}
      style={FONT}
      title={column.title}
    >
      <span
        className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded text-white"
        style={{ backgroundColor: muted ? "#9699A6" : colorFor(column.id) }}
      >
        <Icon size={12} />
      </span>
      <span className="truncate">{column.title}</span>
    </span>
  );
}

function ChipList({ columns, muted }: { columns: BoardColumn[]; muted?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1">
      {columns.map((column) => (
        <ColumnChip key={column.id} column={column} muted={muted} />
      ))}
    </div>
  );
}

const Note = ({ children, tone }: { children: React.ReactNode; tone?: "warn" | "error" }) => (
  <p
    className={`m-0 text-xs leading-[1.45] ${
      tone === "warn" ? "text-[#B35900]" : tone === "error" ? "text-[#D83A52]" : "text-[#676879]"
    }`}
    style={FONT}
  >
    {children}
  </p>
);

export interface SharedColumnsPreviewProps {
  boardId: string | number;
  boardName: string;
  group: Pick<GroupShape, "id" | "title" | "color">;
  /** Path shown under the title, e.g. workspace and board names. */
  breadcrumbs: string[];
  onBack: () => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
}

/**
 * Last step of the add-item dropdown: shows which of this board's columns will sync to the
 * chosen board before the item is shared. Sharing behaves exactly as before; this only previews.
 */
export default function SharedColumnsPreview({
  boardId,
  boardName,
  group,
  breadcrumbs,
  onBack,
  onConfirm,
  confirmDisabled,
}: SharedColumnsPreviewProps) {
  const { currentBoard } = useBoardAndItems();
  const { split, error, isLoading, retry } = useSharedColumnsPreview(currentBoard, boardId);
  const [showSkipped, setShowSkipped] = useState(false);

  const supportedCount = split ? split.shared.length + split.noMatch.length : 0;
  const skippedCount = split ? split.noMatch.length + split.unsupported.length : 0;

  let body: React.ReactNode;
  if (error) {
    body = (
      <>
        <Note tone="error">
          Couldn't load the columns for {boardName}. You can still add the item; columns with the
          same name and type will sync.
        </Note>
        <button
          type="button"
          onClick={retry}
          className="self-start border-0 bg-transparent p-0 text-xs text-[#0073EA] hover:underline cursor-pointer"
          style={FONT}
        >
          Try again
        </button>
      </>
    );
  } else if (isLoading || !split) {
    body = (
      <>
        <div className="flex gap-1" aria-hidden>
          {[70, 90, 60].map((width) => (
            <Skeleton key={width} type="rectangle" width={width} height={24} />
          ))}
        </div>
        <Note>Checking columns on {boardName}…</Note>
      </>
    );
  } else if (split.shared.length === 0) {
    body = (
      <Note tone="warn">
        No columns will sync. {boardName} has no columns with the same name and type as this board.
        Only the item name will be shared.
      </Note>
    );
  } else {
    body = (
      <>
        <ChipList columns={split.shared} />
        {skippedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowSkipped((open) => !open)}
            className="self-start border-0 bg-transparent p-0 text-xs text-[#0073EA] hover:underline cursor-pointer"
            style={FONT}
            aria-expanded={showSkipped}
          >
            {showSkipped ? "Hide" : "Show"} {skippedCount} column{skippedCount === 1 ? "" : "s"}{" "}
            that won't sync
          </button>
        )}
        {showSkipped && split.noMatch.length > 0 && (
          <>
            <Note>
              <b>No matching column on {boardName}</b>
            </Note>
            <ChipList columns={split.noMatch} muted />
            <Note>Add a column with the same name and type to {boardName} to sync it.</Note>
          </>
        )}
        {showSkipped && split.unsupported.length > 0 && (
          <>
            <Note>
              <b>Column type not supported</b>
            </Note>
            <ChipList columns={split.unsupported} muted />
            <Note>SIMB doesn't sync these column types.</Note>
          </>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col w-full menu" data-testid="shared-columns-preview">
      <div style={HEADER_PADDING} className={HEADER_CLASS}>
        <button
          type="button"
          onClick={onBack}
          className="flex w-full flex-row items-center gap-1 mb-2 cursor-pointer rounded border-0 bg-transparent hover:bg-[#6768791a] pr-2 pl-1 py-1 text-left"
        >
          <DropdownChevronLeft />
          <h4>Review and add</h4>
        </button>
        <nav
          aria-label="Breadcrumb"
          className="flex items-center overflow-hidden whitespace-nowrap text-sm text-[#676879]"
        >
          {breadcrumbs.map((crumb, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <NavigationChevronRight size={14} className="mx-0.5 shrink-0" aria-hidden />
              )}
              <span
                className={`truncate ${index === breadcrumbs.length - 1 ? "font-medium text-[#323338]" : ""}`}
              >
                {crumb}
              </span>
            </Fragment>
          ))}
        </nav>
      </div>

      <div className="flex flex-col gap-2 px-2 pt-3">
        <div
          className="flex items-center gap-2 rounded-md border border-solid border-[#E6E9EF] px-2.5 py-2 text-[13px]"
          style={FONT}
        >
          <span className="w-11 shrink-0 text-[#676879]">Board</span>
          <span className="truncate">{boardName}</span>
        </div>
        <div
          className="flex items-center gap-2 rounded-md border border-solid border-[#E6E9EF] px-2.5 py-2 text-[13px]"
          style={FONT}
        >
          <span className="w-11 shrink-0 text-[#676879]">Group</span>
          <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: group.color }} />
          <span className="truncate">{group.title}</span>
        </div>

        <h5 className="m-0 mt-1 flex items-center gap-2 text-[13px] font-semibold" style={FONT}>
          Columns that will sync
          {split && !error && (
            <span
              className={`rounded-full px-[7px] py-px text-[11px] font-semibold ${
                split.shared.length ? "bg-[#D6F5E6] text-[#00854D]" : "bg-[#FDEBDC] text-[#B35900]"
              }`}
            >
              {split.shared.length ? `${split.shared.length} of ${supportedCount}` : "0"}
            </span>
          )}
        </h5>
        {body}
      </div>

      {/* Pinned to the bottom of the dropdown's scroll area so Add item stays reachable with long column lists. */}
      <div className="sticky bottom-0 z-[1] mt-3 flex justify-end gap-2 border-0 border-t border-solid border-[#E6E9EF] bg-[#FFFFFF] px-2 py-3">
        <Button size="small" kind="tertiary" onClick={onBack}>
          Back
        </Button>
        <Button size="small" onClick={onConfirm} disabled={confirmDisabled || isLoading}>
          Add item
        </Button>
      </div>
    </div>
  );
}
